import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { SiteContentProvider } from "@/context/SiteContentContext";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Tours from "@/pages/Tours";
import TourDetail from "@/pages/TourDetail";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import Contact from "@/pages/Contact";
import Testimonials from "@/pages/Testimonials";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import CustomPage from "@/pages/CustomPage";
import ThemePage from "@/pages/ThemePage";
import GA4 from "@/components/GA4";
import ScrollToTop from "@/components/ScrollToTop";

// Legal pages
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Cancellation from "@/pages/legal/Cancellation";
import Insurance from "@/pages/legal/Insurance";

function App() {
  return (
    <HelmetProvider>
      <CurrencyProvider>
        <SiteContentProvider>
          <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GA4 />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/tours" element={<Tours />} />
            <Route path="/tours/:slug" element={<TourDetail />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/destinations/:slug" element={<DestinationDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route path="/profile" element={<Profile />} />
            {/* Themed landing pages (yoga, wildlife, festivals, ...) */}
            <Route path="/themes/:slug" element={<ThemePage />} />
            {/* Legal */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cancellation" element={<Cancellation />} />
            <Route path="/insurance" element={<Insurance />} />
            {/* Admin-created custom pages (catch-all — must be near the end) */}
            <Route path="/pages/:slug" element={<CustomPage />} />
            {/* Legacy Stripe URLs redirect home (search engines / old emails may still point here) */}
            <Route path="/checkout/*" element={<Navigate to="/tours" replace />} />
            {/* 404 fallback for any other unknown route */}
            <Route path="*" element={
              <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="route-404">
                <div className="text-center">
                  <div className="eyebrow text-deepred mb-4">404</div>
                  <h1 className="font-serif text-4xl md:text-5xl mb-4">Page not found</h1>
                  <p className="text-foreground/70 mb-8 max-w-md mx-auto">The link you followed may be broken or the page may have been moved.</p>
                  <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-deepred text-white text-sm uppercase tracking-wider hover:bg-saffron transition-colors">Back to Home</a>
                </div>
              </div>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            className: "font-sans",
            style: {
              background: "#FFFFFF",
              color: "#1A1A1A",
              border: "1px solid #F5EFE0",
            },
          }}
        />
          </AuthProvider>
        </SiteContentProvider>
      </CurrencyProvider>
    </HelmetProvider>
  );
}

export default App;
